import React from 'react'
import { CalendarClock, GripVertical, Layers } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Tooltip } from '../../../shared/components/ui/Tooltip'
import { IssueTypeIcon } from './ListRow'
import {
  PRIORITY_ICON,
  PRIORITY_LABEL,
  PRIORITY_TONE,
  TYPE_BADGE_TONE,
  TYPE_LABEL,
  chipToneFor,
  dueUrgency,
  formatDueDate,
  type EpicRef,
  type SubtaskStats,
} from '../boardUtils'
import type { Issue, WorkflowStatus } from '../api'

export interface BacklogIssueRowProps {
  issue: Issue
  sectionId: string
  assigneeName?: string
  readOnly?: boolean
  selected?: boolean
  onToggleSelect?: (shiftKey: boolean) => void
  onClick: () => void
  epic?: EpicRef | null
  subtaskStats?: SubtaskStats
  statuses?: WorkflowStatus[]
  onUpdateStatus?: (issueId: string, statusId: string, category: string) => void
}

const MAX_VISIBLE_LABELS = 2

export function BacklogIssueRow({
  issue,
  sectionId,
  assigneeName,
  readOnly = false,
  selected = false,
  onToggleSelect,
  onClick,
  epic,
  subtaskStats,
  statuses,
  onUpdateStatus,
}: BacklogIssueRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: issue.id,
    data: { type: 'row', sectionId },
    disabled: readOnly,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 20 : 1,
  }

  const PriorityIcon = PRIORITY_ICON[issue.priority] || PRIORITY_ICON.medium
  const urgency = dueUrgency(issue.due_date, issue.status_category === 'done')
  const labels = issue.tags ?? issue.labels ?? []
  const visibleLabels = labels.slice(0, MAX_VISIBLE_LABELS)
  const hiddenLabelCount = labels.length - visibleLabels.length

  const initials = assigneeName
    ? assigneeName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : ''

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={`group flex items-center gap-3 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm transition-colors hover:bg-[#F4F5F7]/80 dark:hover:bg-slate-800/60 cursor-pointer ${
        selected ? 'bg-blue-50/70 border-blue-200 dark:bg-blue-950/40 dark:border-blue-900' : ''
      } ${isDragging ? 'shadow-lg ring-1 ring-blue-500/40 rounded' : ''}`}
    >
      {/* 1. Drag Handle */}
      {!readOnly && (
        <span
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="cursor-grab touch-none text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 hover:text-gray-700 dark:hover:text-slate-200 active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </span>
      )}

      {/* 2. Checkbox for bulk actions */}
      {onToggleSelect && (
        <input
          type="checkbox"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => onToggleSelect((e.nativeEvent as MouseEvent).shiftKey)}
          className="h-4 w-4 rounded-[3px] border border-gray-400/80 text-blue-600 focus:ring-0 focus:outline-none cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-600 shrink-0"
        />
      )}

      {/* 3. Issue Type Icon */}
      <div className="shrink-0 flex items-center" title={`Type: ${issue.type}`}>
        <IssueTypeIcon type={issue.type} />
      </div>

      {/* 4. Issue Key */}
      <span className="w-16 shrink-0 text-xs font-semibold text-gray-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {issue.key}
      </span>

      {/* 5. Epic Chip */}
      {epic && (
        <span
          onClick={(e) => e.stopPropagation()}
          className={`hidden shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold sm:inline-flex ${chipToneFor(
            epic.id,
          )}`}
        >
          <Layers className="h-2.5 w-2.5" />
          {epic.key}
        </span>
      )}

      {/* 6. Title */}
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[#172B4D] dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {issue.title}
      </span>

      {/* 7. Subtasks count */}
      {subtaskStats && subtaskStats.total > 0 && (
        <span className="hidden shrink-0 rounded bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] font-medium text-gray-600 dark:text-slate-400 md:inline">
          {subtaskStats.done}/{subtaskStats.total}
        </span>
      )}

      {/* 8. Labels */}
      {visibleLabels.length > 0 && (
        <span className="hidden shrink-0 items-center gap-1 md:inline-flex">
          {visibleLabels.map((label) => (
            <span
              key={label}
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${chipToneFor(label)}`}
            >
              {label}
            </span>
          ))}
          {hiddenLabelCount > 0 && (
            <span className="text-[10px] font-medium text-gray-500 dark:text-slate-400">
              +{hiddenLabelCount}
            </span>
          )}
        </span>
      )}

      {/* 9. Due date */}
      {issue.due_date && (
        <span
          className={`hidden items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium sm:inline-flex ${
            urgency === 'overdue'
              ? 'bg-[#FFEBE6] border border-[#FFBDAD] text-[#DE350B]'
              : urgency === 'soon'
              ? 'bg-[#FFF0B3] border border-[#FFE380] text-[#172B4D]'
              : 'border border-gray-200/90 bg-white text-gray-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
          }`}
        >
          <CalendarClock className="h-3 w-3" />
          {formatDueDate(issue.due_date)}
        </span>
      )}

      {/* Status Selector */}
      {statuses && statuses.length > 0 && (
        <div
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="shrink-0"
        >
          <select
            value={issue.status}
            onChange={(e) => {
              e.stopPropagation()
              const targetStatusId = e.target.value
              const targetStatus = statuses.find((s) => s.id === targetStatusId)
              onUpdateStatus?.(issue.id, targetStatusId, targetStatus?.category || 'todo')
            }}
            className={`text-[10px] font-bold uppercase tracking-wider rounded px-2 py-0.5 border transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              issue.status_category === 'done' || (statuses.find((s) => s.id === issue.status)?.category === 'done')
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
                : issue.status_category === 'in_progress' || (statuses.find((s) => s.id === issue.status)?.category === 'in_progress')
                ? 'border-blue-300 bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800'
                : 'border-slate-200 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
            }`}
          >
            {statuses.map((s) => (
              <option key={s.id} value={s.id} className="bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                {s.name}
              </option>
            ))}
            {!statuses.some((s) => s.id === issue.status) && (
              <option value={issue.status} className="bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                {statuses.find((s) => s.id === issue.status)?.name || issue.status}
              </option>
            )}
          </select>
        </div>
      )}

      {/* 10. Priority Icon */}
      <div className="shrink-0 flex items-center">
        <Tooltip content={`${PRIORITY_LABEL[issue.priority] || 'Medium'} priority`}>
          <PriorityIcon className={`h-4 w-4 shrink-0 ${PRIORITY_TONE[issue.priority] || 'text-amber-500'}`} />
        </Tooltip>
      </div>

      {/* 11. Story points */}
      {(issue.story_points != null || issue.estimatedHours != null) && (
        <span className="flex h-5 min-w-[20px] px-1.5 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 text-[10px] font-semibold text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700">
          {issue.story_points ?? issue.estimatedHours}
        </span>
      )}

      {/* 12. Assignee Avatar */}
      <div className="shrink-0 w-6 flex justify-end">
        {assigneeName ? (
          <Tooltip content={assigneeName}>
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#00B8D9] text-white text-[10px] font-bold shadow-xs">
              {initials}
            </div>
          </Tooltip>
        ) : (
          <div
            className="h-6 w-6 rounded-full border border-dashed border-gray-300 dark:border-slate-700 flex items-center justify-center text-[10px] text-gray-400"
            title="Unassigned"
          >
            ?
          </div>
        )}
      </div>
    </div>
  )
}
